/**
 * GripHandle — uchwyt przeciągania (kropki) dla sortowania kolumn w edytorze układu tabeli.
 *
 * `<div role="button" tabIndex={0}>` zamiast natywnego `<button>` — natywny guzik konsumuje
 * `pointerdown` w sposób, który blokuje gest dnd-kit (ta sama lekcja co w `DragHandle`
 * kreatora modułów). `attributes` i `listeners` pochodzą z `useSortable`. Brak
 * `attributes`/`listeners` albo `disabled` = uchwyt wyszarzony i nieaktywny (np. gdy w panelu
 * „Kolumny" wpisano frazę wyszukiwania — przeciąganie po przefiltrowanej liście mijałoby się
 * z kolejnością rzeczywistą).
 *
 * `touch-none` jest wymagane, by PointerSensor działał na ekranach dotykowych.
 */

import type { DraggableAttributes } from '@dnd-kit/core';
import type { useSortable } from '@dnd-kit/sortable';
import { GripVertical } from 'lucide-react';

import { cn } from '@/lib/utils';

/** Typ `listeners` z `useSortable` — `@dnd-kit/core` nie eksportuje go z głównego modułu. */
type SortableListeners = ReturnType<typeof useSortable>['listeners'];

export interface GripHandleProps {
  /** Tekst dla czytnika ekranu (np. „Zmień kolejność kolumny Cena"). */
  ariaLabel: string;
  /** `useSortable().attributes` (niosą też `role="button"` i `tabIndex`). */
  attributes?: DraggableAttributes;
  /** `useSortable().listeners`. */
  listeners?: SortableListeners;
  /** Uchwyt widoczny, ale nieaktywny. */
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function GripHandle({
  ariaLabel,
  attributes,
  listeners,
  disabled = false,
  className,
  'data-testid': testId,
}: GripHandleProps) {
  const inactive = disabled || attributes === undefined;

  if (inactive) {
    return (
      <div
        role="button"
        tabIndex={-1}
        aria-label={ariaLabel}
        aria-disabled="true"
        data-testid={testId}
        data-disabled="true"
        className={cn(
          'shrink-0 cursor-not-allowed rounded p-0.5 text-text-muted opacity-40',
          className,
        )}
      >
        <GripVertical className="h-4 w-4" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div
      aria-label={ariaLabel}
      data-testid={testId}
      {...attributes}
      {...listeners}
      className={cn(
        'shrink-0 cursor-grab touch-none rounded p-0.5 text-text-muted hover:bg-bg-tertiary',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500',
        className,
      )}
    >
      <GripVertical className="h-4 w-4" aria-hidden="true" />
    </div>
  );
}
